import React, { FC, Fragment, ReactNode } from 'react';
import { MutationFunction } from 'react-apollo';
import { DocumentNode } from 'graphql';

import { FollowComponent, UnfollowComponent } from 'api';
import { FeedKey, Group, MutationFollowArgs, MutationUnfollowArgs, PublicUserProfile } from 'api';
import { followResponse, unfollowResponse } from 'apollo/responses';
import { Error } from 'components';
import { IWithFollowed, withFollowed } from 'providers/FollowedContext';
import { useCurrentUser } from 'hooks';
import { isGroupOwnerOrModerator } from 'features/groups/helpers';
import { followToggle } from 'apollo/reducers';

export interface IFollowToggleRenderProps {
  disableToggle?: boolean;
  isFollowing: boolean;
  loading: boolean;
  onClick: () => Promise<any>;
}

interface IFollowsMutations {
  follow: MutationFunction;
  unfollow: MutationFunction;
}

type FollowsMutationsArgs = MutationFollowArgs | MutationUnfollowArgs;

interface IProps {
  source: Group | PublicUserProfile;
  type: FeedKey.Group | FeedKey.User;
  children: (props: IFollowToggleRenderProps) => ReactNode;
  refetchQueries?: Array<{ query: DocumentNode; variables?: object }>;
}

interface IPropsEnhanced extends IProps, IWithFollowed {}

const FollowToggle: FC<IPropsEnhanced> = props => {
  const { children, followed, source, type, refetchQueries } = props;
  const currentUser = useCurrentUser();

  const isFollowing = followed.some(follow => follow.id === source.id);

  function hasExceptions() {
    // Users can't follow toggle themselves
    if (source.id === currentUser.id) {
      return true;
    }

    // Group owners and moderators can't follow toggle
    if (isGroupOwnerOrModerator(currentUser, source.id)) {
      return true;
    }

    return false;
  }

  function getResponse() {
    source.follower_count = isFollowing ? source.follower_count - 1 : source.follower_count + 1;

    const params = { source, type, currentUser };
    return isFollowing ? unfollowResponse(params) : followResponse(params);
  }

  function handleToggle(mutations: IFollowsMutations) {
    const mutation = isFollowing ? mutations.unfollow : mutations.follow;
    const variables: FollowsMutationsArgs = { id: source.id, key: type };
    const optimisticResponse = getResponse();

    return mutation({
      variables,
      refetchQueries,
      optimisticResponse,
      update: (cache, { data }) => {
        followToggle(cache, data, { type, currentUser });
      },
    });
  }

  return (
    <FollowComponent>
      {(follow, followOptions) => (
        <Fragment>
          <Error message={followOptions.error} />
          <UnfollowComponent>
            {(unfollow, unfollowOptions) => {
              const renderProps: IFollowToggleRenderProps = {
                isFollowing,
                disableToggle: hasExceptions(),
                loading: followOptions.loading || unfollowOptions.loading,
                onClick: () => handleToggle({ follow, unfollow }),
              };

              return (
                <Fragment>
                  <Error message={unfollowOptions.error} />
                  {children(renderProps)}
                </Fragment>
              );
            }}
          </UnfollowComponent>
        </Fragment>
      )}
    </FollowComponent>
  );
};

export default withFollowed('followed')(FollowToggle);
